/**
 * @fileoverview WebSocket initialization for real-time chat.
 * @module logic/chat/chatWebSocket
 */

import { logger, sanitizeError } from "../../utils/logger.js";
import { WS_CLOSE_GOING_AWAY, WS_CLOSE_NORMAL } from "./chatConstants.js";

/**
 * Initializes a WebSocket connection for real-time chat.
 * @param {Function} onMessage - Callback for incoming messages
 * @param {Function} onOpen - Callback when connection opens
 * @param {Function} onError - Callback for connection errors
 * @param {Function} onClose - Callback when connection closes
 * @param {Function} onReconnect - Callback for reconnection attempts
 * @returns {WebSocket|null} WebSocket instance or null on error
 */
export const initializeWebSocket = (
  onMessage,
  onOpen,
  onError,
  onClose,
  _onReconnect,
) => {
  logger.info("Initializing WebSocket");
  const token = localStorage.getItem("token");
  if (!token || token === "undefined") {
    return null;
  }
  const wsUrl = `${
    process.env.REACT_APP_WEBSOCKET_API
  }?token=${encodeURIComponent(token)}`;

  try {
    const ws = new WebSocket(wsUrl);

    ws.onopen = () => {
      logger.info("WebSocket Connected Successfully");
      onOpen && onOpen();
    };

    ws.onmessage = onMessage;

    ws.onerror = (error) => {
      logger.error("WebSocket error details:", {
        data: {
          error: error,
          readyState: ws?.readyState,
          url: wsUrl,
        },
      });
      onError && onError(error);
    };

    ws.onclose = (event) => {
      logger.info("WebSocket Disconnected. Code:", {
        data: [event.code, "Reason:", event.reason],
      });

      // Don't reconnect if it's a normal closure or user-initiated
      if (event.code === WS_CLOSE_NORMAL || event.code === WS_CLOSE_GOING_AWAY) {
        logger.info("WebSocket closed normally");
        onClose && onClose(event);
      }
    };

    return ws;
  } catch (error) {
    logger.error("Error initializing WebSocket:", {
      err: sanitizeError(error),
    });
    return null;
  }
};
